import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Clock, Check, PenTool, FileText, Users, ChevronDown, ChevronUp, AlertCircle, CheckCircle, Loader2, Type, AlignLeft, CheckSquare } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatTimeAgo = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (isoStr) => {
  if (!isoStr) return 'N/A';
  return new Date(isoStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });
};

const EventIcon = ({ type }) => {
  if (type === 'signed') return <PenTool className="w-3.5 h-3.5" />;
  if (type === 'fields_created') return <FileText className="w-3.5 h-3.5" />;
  if (type === 'fields_updated') return <FileText className="w-3.5 h-3.5" />;
  return <Clock className="w-3.5 h-3.5" />;
};

const eventColor = (type) => {
  if (type === 'signed') return { dot: 'bg-emerald-500', text: 'text-emerald-400', line: 'border-emerald-500/30' };
  if (type === 'fields_created') return { dot: 'bg-blue-500', text: 'text-blue-400', line: 'border-blue-500/30' };
  if (type === 'fields_updated') return { dot: 'bg-amber-500', text: 'text-amber-400', line: 'border-amber-500/30' };
  return { dot: 'bg-white/30', text: 'text-white/50', line: 'border-white/10' };
};

const SigningAuditDialog = ({ open, onOpenChange, documentId, documentName, token }) => {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedEvent, setExpandedEvent] = useState(null);

  const authHeaders = () => token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
  if (!open || !documentId) return;
  setLoading(true);

  const fetchAudit = async () => {
    try {
      const res = await fetch(`${API_URL}/api/documents/${documentId}/signing-audit`, {
        credentials: 'include', 
        headers: authHeaders()
      });
      if (res.ok) setAudit(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  };

  fetchAudit();
}, [open, documentId, API_URL, authHeaders]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl w-[95vw] max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Signing History
          </DialogTitle>
          <DialogDescription className="text-white/60 truncate">
            {documentName}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto mt-4 space-y-5" data-testid="signing-audit-content">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : !audit ? (
            <div className="text-center py-16">
              <AlertCircle className="w-10 h-10 text-white/20 mx-auto mb-3" />
              <p className="text-white/40 text-sm">Could not load signing audit</p>
            </div>
          ) : (
            <>
              {/* Status Banner */}
              <div className={`flex items-center gap-3 px-4 py-3 rounded-sm border ${audit.all_complete ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}
                data-testid="audit-status-banner">
                {audit.all_complete ? (
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                ) : (
                  <Clock className="w-5 h-5 text-amber-400 flex-shrink-0" />
                )}
                <div>
                  <p className={`text-sm font-medium ${audit.all_complete ? 'text-emerald-300' : 'text-amber-300'}`}>
                    {audit.all_complete ? 'All Signatures Collected' : 'Signing In Progress'}
                  </p>
                  <p className="text-white/40 text-xs">
                    {audit.total_signatures} signature{audit.total_signatures !== 1 ? 's' : ''}
                    {audit.has_template ? ` | ${audit.signing_order} signing` : ''}
                    {audit.assigned_signers?.length > 0 ? ` | ${audit.assigned_signers.filter(s => s.status === 'completed').length}/${audit.assigned_signers.length} signers complete` : ''}
                  </p>
                </div>
              </div>

              {/* Assigned Signers */}
              {audit.assigned_signers?.length > 0 && (
                <div data-testid="assigned-signers-section">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-white/40" />
                    <p className="text-white/50 text-xs font-medium uppercase tracking-wider">Assigned Signers</p>
                  </div>
                  <div className="space-y-1.5">
                    {audit.assigned_signers.map((signer, i) => (
                      <div key={signer.user_id}
                        className={`flex items-center gap-3 px-3 py-2 rounded-sm border ${signer.status === 'completed' ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-white/10 bg-white/[0.02]'}`}
                        data-testid={`signer-${signer.user_id}`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${signer.status === 'completed' ? 'bg-emerald-500/20' : 'bg-white/5'}`}>
                          {signer.status === 'completed'
                            ? <Check className="w-3.5 h-3.5 text-emerald-400" />
                            : <span className="text-white/30 text-[10px] font-bold">{i + 1}</span>
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs font-medium truncate">{signer.name}</p>
                          <p className="text-white/30 text-[10px] truncate">{signer.email}</p>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          {signer.status === 'completed' ? (
                            <p className="text-emerald-400/80 text-[10px]">
                              {signer.signed_at_display || formatTimeAgo(signer.signed_at)}
                            </p>
                          ) : (
                            <span className="text-amber-400/60 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/20">Pending</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Field Definitions */}
              {audit.field_definitions?.length > 0 && (
                <div data-testid="field-definitions-section">
                  <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-2">Template Fields</p>
                  <div className="flex flex-wrap gap-1.5">
                    {audit.field_definitions.map(f => {
                      const colors = f.type === 'signature' ? 'border-blue-500/30 text-blue-400 bg-blue-500/5'
                        : f.type === 'name' ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/5'
                        : f.type === 'text' ? 'border-purple-500/30 text-purple-400 bg-purple-500/5'
                        : f.type === 'checkbox' ? 'border-pink-500/30 text-pink-400 bg-pink-500/5'
                        : 'border-amber-500/30 text-amber-400 bg-amber-500/5';
                      const Icon = f.type === 'signature' ? PenTool : f.type === 'name' ? Type : f.type === 'text' ? AlignLeft : f.type === 'checkbox' ? CheckSquare : Clock;
                      return (
                        <span key={f.field_id} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] border ${colors}`}>
                          <Icon className="w-2.5 h-2.5" /> {f.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div data-testid="audit-timeline">
                <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-3">Timeline</p>
                {audit.events?.length === 0 ? (
                  <p className="text-white/30 text-sm text-center py-6">No signing activity yet</p>
                ) : (
                  <div className="relative pl-6">
                    {/* Vertical line */}
                    <div className="absolute left-[9px] top-2 bottom-2 w-px bg-white/10" />

                    {audit.events.map((event, i) => {
                      const color = eventColor(event.type);
                      const isExpanded = expandedEvent === i;
                      const hasDetails = event.field_values_summary && Object.keys(event.field_values_summary).length > 0;
                      return (
                        <div key={i} className="relative pb-5 last:pb-0" data-testid={`audit-event-${i}`}>
                          {/* Dot */}
                          <div className={`absolute left-[-15px] top-1 w-[18px] h-[18px] rounded-full ${color.dot} flex items-center justify-center z-10`}>
                            <EventIcon type={event.type} />
                          </div>
                          {/* Content */}
                          <div className="ml-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="text-white text-xs font-medium">{event.actor_name}</p>
                                <p className="text-white/50 text-[11px] mt-0.5">{event.description}</p>
                              </div>
                              <span className="text-white/25 text-[10px] flex-shrink-0 pt-0.5">
                                {formatDateTime(event.timestamp)}
                              </span>
                            </div>
                            {hasDetails && (
                              <button onClick={() => setExpandedEvent(isExpanded ? null : i)}
                                className="mt-1.5 flex items-center gap-1 text-[10px] text-white/30 hover:text-white/50 transition-colors"
                                data-testid={`expand-event-${i}`}>
                                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                {isExpanded ? 'Hide' : 'Show'} field values
                              </button>
                            )}
                            {isExpanded && event.field_values_summary && (
                              <div className="mt-2 space-y-1 pl-2 border-l-2 border-white/10">
                                {Object.entries(event.field_values_summary).map(([fid, fv]) => (
                                  <div key={fid} className="flex items-center gap-2 text-[10px]">
                                    <span className="text-white/30">{fv.label || fv.type}:</span>
                                    <span className="text-white/60">
                                      {fv.type === 'checkbox' ? (fv.data === 'True' || fv.data === 'true' ? 'Checked' : 'Unchecked') : fv.data}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SigningAuditDialog;
